const tryAgainContactSupportCopy =
  'Please try again or {{contact}} if you are unable to access your account.';
const tryDifferentSignInMethodSupportCopy =
  'Please try a different sign in method or {{contact}} if you are unable to access your account.';

export interface Copy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const copy: Copy = {
  header: {
    register: {
      preamble: 'Already have an account?',
      link_text: 'Sign in'
    },
    login: {
      preamble: "Don't have an account?",
      link_text: 'Sign up'
    }
  },
  form: {
    newBrandDesign: {
      login_title: 'Log in to your account',
      email_label: 'email',
      preferred_login_title: 'Welcome back',
      login_different_account: 'Login with a different account'
    },
    login_title: 'Sign in to your account',
    login_different_account: 'Sign in with a different account',
    preferred_login_title: 'Welcome back to DigitalOcean',
    preferred_login_details: 'Sign into your account below',
    password_forgot_title: 'Forgot your password',
    password_reset_title: 'Reset your password',
    email_signup_title: 'Sign Up with Email',
    name_label: 'Full Name',
    name_placeholder: 'Enter a name',
    password_label: 'Password',
    email_label: 'Email Address',
    email_placeholder: 'Enter your email address',
    button_text: 'Sign Up',
    forgot_password: 'Forgot Password?',
    or: 'or',
    not_you: 'Not you?',
    contact_support_link: 'contact support',
    register: {
      button_text: 'Sign Up',
      password_placeholder: 'Create a password'
    },
    login: {
      newBrandDesign: {
        button_text: 'Log In'
      },
      button_text: 'Sign In',
      password_placeholder: 'Enter your password',
      after_password: {
        forgot: 'We have emailed you a link to reset your password',
        reset: 'Your password was reset successfully'
      }
    },
    validation: {
      email_required: 'Email Address is required',
      email_valid: 'Email Address must be valid',
      password_required: 'Password is required',
      password_min: `Password must be at least {{VALIDATION.PASSWORD.MIN}} characters long`,
      code_required: 'Verification code required',
      code_digit:
        'Verification code must be a {{VALIDATION.VERIFICATION.DIGITS}} digit number',
      code_character:
        'Verification code must be {{VALIDATION.VERIFICATION.DIGITS}} hex characters',
      code_character_long:
        'Verification code must be {{VALIDATION.VERIFICATION.LONG_CHAR}} hex characters',
      password_unique:
        'Password must contain at least {{VALIDATION.PASSWORD.UNIQUE}} unique characters'
    },
    oauth: {
      title: 'Sign up with',
      google: 'Google',
      github: 'GitHub',
      register: {
        google_button: 'Sign Up with Google',
        github_button: 'Sign Up with GitHub'
      },
      login: {
        google_button: 'Sign In with Google',
        github_button: 'Sign In with GitHub'
      },
      instead: 'instead'
    },
    verification: {
      title: "Verify it's you",
      details:
        'We sent a verification code to your email. Enter the code from the email in the field below.',
      checkbox_label: 'Trust this device for 60 days?',
      button_text: 'Verify Code',
      need_help_text: 'Need help?',
      checkout_text: 'Check out our',
      troubleshooting_link: 'troubleshooting guide',
      try_again_link: 'try again',
      code_label: '{{VALIDATION.VERIFICATION.DIGITS}}-digit code'
    },
    two_factor_auth: {
      title: 'Two-factor authentication',
      backup_title: 'Two-factor back up',
      authenticator: {
        details:
          'Open your authentication app and enter the code for DigitalOcean.',
        no_access: "Can't access your authentication app?",
        forward_link: 'Switch back to app authentication',
        backup_link: 'Use app authentication instead',
        code_label: '{{VALIDATION.VERIFICATION.DIGITS}}-digit code'
      },
      sms: {
        details: 'An SMS with your verification code was sent to your phone.',
        no_access: "Can't receive an SMS?",
        backup_link: 'Send an sms instead',
        forward_link: 'Switch back to sms',
        resend_link: 'Resend code',
        code_label: '{{VALIDATION.VERIFICATION.DIGITS}}-character code'
      },
      recovery_codes: {
        details: 'Enter one of the recovery codes you previously generated.',
        no_access: "Don't have a code?",
        backup_link: 'Enter a recovery code',
        code_label: '{{VALIDATION.VERIFICATION.LONG_CHAR}}-character code'
      },
      button_text: 'Verify Code',
      placeholder_prefix: 'Enter'
    },
    two_factor_recovery: {
      title: 'Two-factor back up'
    },
    password_forgot: {
      title: 'Forgot password?',
      details:
        'Enter the email address associated with your account and we will send you a link to reset your password.',
      label: 'Email address',
      placeholder: 'Enter your email address',
      button_text: 'Request Password Reset',
      loading: 'Emailing you a link'
    },
    password_reset: {
      title: 'Reset password',
      error_title: 'This link has expired',
      details:
        'Your new password must be at least 8 characters long and contain at least 6 unique characters',
      error_details:
        'Password reset links expire after an hour for security purposes, please request another password reset link below to continue',
      label: 'New password',
      placeholder: 'Enter a new password',
      button_text: 'Reset Password',
      loading: 'Submitting'
    }
  },
  credit: {
    title:
      /* eslint-disable-next-line @do/no-hardcoded-prices */
      'Try DigitalOcean with a $100 credit' /* UI_ENG_HARDCODED_CREDIT_PRICE */,
    details:
      /* eslint-disable-next-line @do/no-hardcoded-prices */
      'You will receive a $100 credit (good for 60 days) when you create a new account on DigitalOcean' /* UI_ENG_HARDCODED_CREDIT_PRICE */
  },
  footer: {
    terms_before_link: 'By signing up you agree to the',
    terms_link_text: 'Terms of Service',
    privacy_before_link: 'and',
    privacy_link_text: 'Privacy Policy'
  },
  loading: {
    login: {
      belowButton: 'Signing in'
    },
    registration: {
      belowButton: 'Creating your account'
    }
  },
  title_suffix: 'DigitalOcean',
  error_with_message_title: 'A server error has occured.',
  errors: {
    unknown: {
      title: 'An unknown error has occurred.',
      details:
        'Please try your request again. If the problem persists {{contact}}.'
    },
    password_is_common: {
      title: 'Password problem',
      details: 'Please choose a less-common password.'
    },
    password_is_email: {
      title: 'Password problem',
      details:
        'Please ensure that your password is distinct from your email address.'
    },
    password_is_name: {
      title: 'Password problem',
      details: 'Please ensure that your password is distinct from your name.'
    },
    error_invalid_oauth_request: {
      title: 'OAuth Error',
      details: `Sorry, your oauth request is invalid. ${tryDifferentSignInMethodSupportCopy}`
    },
    error_registering_user: {
      title: 'OAuth Error',
      details: `Sorry, there was an error registering your oauth user. ${tryAgainContactSupportCopy}`
    },
    error_signing_in_user: {
      title: 'OAuth Error',
      details: `Sorry, there was an error signing-in your oauth user. ${tryAgainContactSupportCopy}`
    },
    error_user_not_found: {
      title: null,
      details: `An account with this email address was not found. ${tryAgainContactSupportCopy}`
    },
    error_google_auth_mismatch: {
      title: null,
      details: `An account with this Google sign in was not found. ${tryDifferentSignInMethodSupportCopy}`
    },
    error_github_auth_mismatch: {
      title: null,
      details: `An account with this GitHub sign in was not found. ${tryDifferentSignInMethodSupportCopy}`
    },
    email_exists: {
      title: 'Email Exists',
      details:
        'Sorry, that email address is already associated with an account.'
    },
    email_is_invalid: {
      title: 'Invalid email',
      details: 'Sorry, that email address is not valid.'
    },
    incorrect_email_or_password: {
      title: 'Incorrect email or password',
      details: `The email or password you entered is incorrect. ${tryAgainContactSupportCopy}`
    },
    'sign in method is not email/password': {
      title: null,
      details: `An account with this email address was not found. ${tryDifferentSignInMethodSupportCopy}`
    },
    invalid_verification_code: {
      title: 'Incorrect code',
      details: `Incorrect verification code. ${tryAgainContactSupportCopy}`
    },
    expired_verification_code: {
      title: 'Code Expired',
      details: 'Please check your email for a new code and try again'
    },
    incorrect_two_factor: {
      title: 'Incorrect code',
      details: `The code you entered is incorrect. ${tryAgainContactSupportCopy}`
    },
    too_long: {
      title: 'Incorrect email',
      details: 'Sorry, that email address is too long.'
    },
    request_blocked: {
      title: 'Request blocked',
      details: 'Please modify your request or {{contact}} for more information'
    },
    arkose_loading_failed: {
      title: null,
      details: 'Please unblock third-party requests in order to proceed.'
    },
    arkose_failed: {
      title: null,
      details:
        "We're sorry, but based on the information you have given us we cannot allow you to make an account at this time."
    },
    not_ownder_or_biller: {
      title: 'Promotion cannot be applied to this account ',
      details:
        'Only admin users can apply promotions to this account. Please update your permissions or create a new team to apply this promotion.'
    },
    not_eligible: {
      title: 'GitHub student pack not available',
      details:
        'Your GitHub account is not currently enrolled in the GitHub Student pack. Please try a different GitHub account or contact support if you believe this is an error.'
    },
    already_applied_student_pack: {
      title: 'Promotion cannot be applied to this account',
      details:
        'The GitHub Student Pack promotion has already been applied to this account. Please select a different team or create a new one.'
    },
    already_has_student_pack: {
      title: 'Promotion cannot be applied to this account',
      details:
        'The GitHub Student Pack promotion has already been applied to another team. Please switch to the team it was applied to use your credits.'
    }
  },
  survey_text: 'please take this brief survey',
  deactivation_message:
    'Your account has been successfully deactivated. If you would like to tell us how we could have served you better, '
};

export default copy;
